import React, { useMemo, useState, useEffect } from 'react';
import { Container, Spinner, EmptyContent, Alert, Flex } from '@/components/ui';
import { withPermissions } from '@/components/withPermissions';
import { PERMISSIONS } from '@/types/permissions';
import { useStudyForms } from './hooks/useStudyForms';
import { useAuth } from '@/contexts/AuthContext';
import { buildFormTree } from './components/utils/formTreeUtils';
import { FormTreeWithTemplates } from './components/FormTreeWithTemplates';
import { DemoPreviewToggle } from './components/DemoPreviewToggle';
import { DemoDataSettingsPopover } from './components/DemoDataSettingsPopover';
import { downloadCsvArchive } from './utils/csvArchiveGenerator';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { DemoDataSettings } from './types/demoDataSettings';
import { DEFAULT_DEMO_DATA_SETTINGS } from './types/demoDataSettings';
import {
  loadDemoDataSettings,
  saveDemoDataSettings,
} from './utils/demoDataSettingsStorage';

const StudyFormsTemplates: React.FC = () => {
  const { user: profile } = useAuth();
  const studyId = profile?.current_study?.id;
  const [previewWithDemo, setPreviewWithDemo] = useState(true);
  
  // Load settings from localStorage on mount
  const [demoDataSettings, setDemoDataSettings] = useState<DemoDataSettings>(() => {
    const saved = loadDemoDataSettings();
    return saved || DEFAULT_DEMO_DATA_SETTINGS;
  });

  // Save settings to localStorage whenever they change
  useEffect(() => {
    saveDemoDataSettings(demoDataSettings);
  }, [demoDataSettings]);

  const handleSettingsChange = (settings: DemoDataSettings) => {
    setDemoDataSettings(settings);
  };

  // Load study forms
  const {
    forms: studyForms,
    loading: loadingForms,
    error: formsError,
    hasForms,
  } = useStudyForms({
    enabled: !!studyId,
    autoLoad: true,
  });

  // Build form tree
  const formTree = useMemo(() => {
    if (!studyForms || studyForms.length === 0) {
      return [];
    }
    return buildFormTree(studyForms);
  }, [studyForms]);

  const handleDownloadAll = async (type: 'template' | 'demo') => {
    if (formTree.length === 0) {
      return;
    }
    await downloadCsvArchive(formTree, type, studyForms, type === 'demo' ? demoDataSettings : undefined);
  };

  if (loadingForms) {
    return (
      <Container pt="2" pb="2" size="expand">
        <EmptyContent
          title="Loading study structure..."
          icon={<Spinner size="2" />}
          description="Please wait while we load the study forms structure."
        />
      </Container>
    );
  }

  if (formsError) {
    return (
      <Container pt="2" pb="2" size="expand">
        <EmptyContent
          state="error"
          withIconBox
          icon={<IconAlertTriangle />}
          title="Failed to load study structure"
          description={formsError.message || 'Unable to load study forms. Please try again or contact support.'}
        />
      </Container>
    );
  }

  if (!hasForms && studyId) {
    return (
      <Container pt="2" pb="2" size="expand">
        <Alert color="warning" title="Study structure not found">
          <p>
            This study does not have any forms configured. Please configure
            study forms before downloading templates.
          </p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container pt="2" pb="2" size="expand" className="study-forms-templates-page">
      <Flex direction="column" gap="3">
        {/* Preview toggle */}
        <Flex justify="end" align="center" gap="2">
          <DemoPreviewToggle
            enabled={previewWithDemo}
            onChange={setPreviewWithDemo}
            numberOfRows={demoDataSettings.numberOfRows}
          />
          {previewWithDemo && (
            <DemoDataSettingsPopover
              settings={demoDataSettings}
              onSettingsChange={handleSettingsChange}
            />
          )}
        </Flex>

        {/* Forms tree */}
        <FormTreeWithTemplates
          tree={formTree}
          allForms={studyForms}
          previewWithDemo={previewWithDemo}
          onPreviewWithDemoChange={setPreviewWithDemo}
          onDownloadAll={handleDownloadAll}
          demoDataSettings={previewWithDemo ? demoDataSettings : undefined}
          onDemoDataSettingsChange={handleSettingsChange}
        />
      </Flex>
    </Container>
  );
};

export const StudyFormsTemplatesPage = withPermissions(StudyFormsTemplates, {
  requiredPermissions: [PERMISSIONS.DATA_ACCESS],
  showRoleSwitcher: true,
});

